import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ApolloProvider, useQuery } from "@apollo/client/react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ThemeProvider, createTheme } from "@mui/material/styles";

import { client } from "./apollo/client";
import themestyle from "./theme";

import Login from "./pages/login";
import Home from "./pages/home";
import AdminManagement from "./pages/admin";
import ForgotPassowrd from "./pages/forgotpassword";
import ResetPassword from "./pages/resetpassword";
import Signup from "./pages/signup";
import ClassManagement from "./pages/classes";
import InstructorManagement from "./pages/instructors";
import InstructorReport from "./pages/InstructorReport";
import AttendancePage from "./pages/attendance";
import AttendanceDetailsPage from "./pages/attendance-details";
import StudentManagement from "./pages/Student";
import StudentReport from "./pages/StudentReport";
import PassesManagement from "./pages/Passes";
import OfferManagement from "./pages/offers";
import EventManagement from "./pages/events";
import NotificationManagement from "./pages/notifications";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import { SidebarProvider } from "./components/sidebarcontext";
import { SessionContextProvider } from "./context/SessionContext";
import { CartProvider } from "./context/CartContext";
import AccountPage from "./pages/settings";
import BillingPage from "./pages/billing";
import ReportsPage from "./pages/reports";
import NotFound from "./components/NotFound";
import AdminOnboardModel from "./components/AdminOnboardModel";
import { ME } from "./graphql/query";
import PaymentCallback from "./pages/PaymentCallback";
import PassReport from "./components/PassReport";
import EventReport from "./components/EventReport";

// App-wide MUI theme: primary = brand green, so every MUI "primary" state (field focus
// borders, the date/time picker icon, selected dropdown cells, OK/CANCEL actions) is green
// instead of the default blue — consistently across all fields.
const appTheme = createTheme({
  palette: {
    primary: { main: themestyle.colors.activeGreen },
  },
});

const OnboardingOverlay: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data } = useQuery(ME, {
    fetchPolicy: "network-only",
  });

  let storedUser: any = null;
  try {
    storedUser = JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    storedUser = null;
  }

  const role = data?.me?.role || storedUser?.role;
  const isSuperAdmin = role === "SUPER_ADMIN";

  const needsOnboarding =
    !isSuperAdmin &&
    (data?.me?.isFirstLoggedIn === true ||
    storedUser?.isFirstLoggedIn === true);

  return (
    <>
      {children}
      {needsOnboarding && <AdminOnboardModel />}
    </>
  );
};


const App = () => {
  return (
    <ThemeProvider theme={appTheme}>
    <Router>
      <SessionContextProvider>
        <SidebarProvider>
          <CartProvider>
          <ApolloProvider client={client}>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              localeText={{
                // Empty-field placeholders that mirror the native inputs: "dd-mm-yyyy" / "--:--".
                fieldDayPlaceholder: () => "dd",
                fieldMonthPlaceholder: () => "mm",
                fieldYearPlaceholder: () => "yyyy",
                fieldHoursPlaceholder: () => "--",
                fieldMinutesPlaceholder: () => "--",
              }}
            >
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgotpassword" element={<ForgotPassowrd />} />
              <Route path="/resetpassword/:token" element={<ResetPassword />} />
              <Route path="/payment/callback" element={<PaymentCallback />} />

              {/* Protected Routes — redirects to "/" if not logged in */}
              <Route element={<ProtectedRoute />}>
                <Route
                  path="/dashboard"
                  element={
                    <Layout title="Dashboard">
                      <Home />
                    </Layout>
                  }
                />
                <Route
                  path="/DashboardLayout"
                  element={
                    <Layout title="Dashboard">
                      <Home />
                    </Layout>
                  }
                />
                <Route
                  path="/home"
                  element={
                    <Layout title="Home">
                      <Home />
                    </Layout>
                  }
                />
                <Route
                  path="/billing"
                  element={
                    <Layout title="Billing Dashboard">
                      <BillingPage />
                    </Layout>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <Layout title="Reports">
                      <ReportsPage />
                    </Layout>
                  }
                />
                <Route
                  path="/offers"
                  element={
                    <Layout title="Offers">
                      <OfferManagement />
                    </Layout>
                  }
                />
                <Route
                  path="/offers/add"
                  element={
                    <Layout title="Offers">
                      <OfferManagement />
                    </Layout>
                  }
                />
                <Route
                  path="/offers/:mode/:id"
                  element={
                    <Layout title="Offers">
                      <OfferManagement />
                    </Layout>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <Layout title="Admin Management">
                      <AdminManagement />
                    </Layout>
                  }
                />
                <Route
                  path="/classes"
                  element={
                    <Layout title="Classes">
                      <OnboardingOverlay>
                        <ClassManagement />
                      </OnboardingOverlay>
                    </Layout>
                  }
                />
                <Route
                  path="/classes/add"
                  element={
                    <Layout title="Classes">
                      <OnboardingOverlay>
                        <ClassManagement />
                      </OnboardingOverlay>
                    </Layout>
                  }
                />
                <Route
                  path="/classes/:mode/:id"
                  element={
                    <Layout title="Classes">
                      <OnboardingOverlay>
                        <ClassManagement />
                      </OnboardingOverlay>
                    </Layout>
                  }
                />
                <Route
                  path="/instructors"
                  element={
                    <Layout title="Instructors">
                      <InstructorManagement />
                    </Layout>
                  }
                />
                <Route
                  path="/instructors/add"
                  element={
                    <Layout title="Instructors">
                      <InstructorManagement />
                    </Layout>
                  }
                />
                <Route
                  path="/instructors/report/:id"
                  element={
                    <Layout title="Instructor Report">
                      <InstructorReport />
                    </Layout>
                  }
                />
                <Route
                  path="/students"
                  element={
                    <Layout title="Students">
                      <StudentManagement />
                    </Layout>
                  }
                />
                <Route
                  path="/students/add"
                  element={
                    <Layout title="Students">
                      <StudentManagement />
                    </Layout>
                  }
                />
                <Route
                  path="/students/report/:id"
                  element={
                    <Layout title="Student Report">
                      <StudentReport />
                    </Layout>
                  }
                />
                <Route
                  path="/students/:mode/:id"
                  element={
                    <Layout title="Students">
                      <StudentManagement />
                    </Layout>
                  }
                />
                <Route
                  path="/passes"
                  element={
                    <OnboardingOverlay>
                      <Layout title="Passes">
                        <PassesManagement />
                      </Layout>
                    </OnboardingOverlay>
                  }
                />
                <Route
                  path="/passes/report/:id"
                  element={
                    <OnboardingOverlay>
                      <Layout title="Pass Report">
                        <PassReport />
                      </Layout>
                    </OnboardingOverlay>
                  }
                />
                <Route
                  path="/passes/add"
                  element={
                    <Layout title="Passes">
                      <OnboardingOverlay>
                        <PassesManagement />
                      </OnboardingOverlay>
                    </Layout>
                  }
                />
                <Route
                  path="/passes/:mode/:id"
                  element={
                    <Layout title="Passes">
                      <OnboardingOverlay>
                        <PassesManagement />
                      </OnboardingOverlay>
                    </Layout>
                  }
                />
                <Route
                  path="/events"
                  element={
                    <OnboardingOverlay>
                      <Layout title="Events">
                        <EventManagement />
                      </Layout>
                    </OnboardingOverlay>
                  }
                />
                <Route
                  path="/events/report/:id"
                  element={
                    <OnboardingOverlay>
                      <Layout title="Event Report">
                        <EventReport />
                      </Layout>
                    </OnboardingOverlay>
                  }
                />
                <Route
                  path="/events/add"
                  element={
                    <Layout title="Events">
                      <OnboardingOverlay>
                        <EventManagement />
                      </OnboardingOverlay>
                    </Layout>
                  }
                />
                <Route
                  path="/events/:mode/:id"
                  element={
                    <Layout title="Events">
                      <OnboardingOverlay>
                        <EventManagement />
                      </OnboardingOverlay>
                    </Layout>
                  }
                />
                <Route
                  path="/calendar"
                  element={
                    <Layout title="Calendar">
                      <OnboardingOverlay>
                        <AttendancePage />
                      </OnboardingOverlay>
                    </Layout>
                  }
                />
                <Route
                  path="/calendar/:id"
                  element={
                    <Layout title="Calendar Details">
                      <OnboardingOverlay>
                        <AttendanceDetailsPage />
                      </OnboardingOverlay>
                    </Layout>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <Layout title="Settings">
                      <OnboardingOverlay>
                        <AccountPage />
                      </OnboardingOverlay>
                    </Layout>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <Layout title="Notifications">
                      <NotificationManagement />
                    </Layout>
                  }
                />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
            </LocalizationProvider>
          </ApolloProvider>
          </CartProvider>
        </SidebarProvider>
      </SessionContextProvider>
    </Router>
    </ThemeProvider>
  );
};

export default App;
